<script setup lang="ts">
import {ref, computed, watch} from 'vue';
import {Icon} from '@iconify/vue';
import {watchDebounced} from '@vueuse/core';
import {
  getAccidentalsDescription,
  getKeyDisplayName,
  KEY_SIGNATURES,
} from '@/composables/musicXmlDom';
import {injectScoreEditContext} from '@/components/musicxml-preview/editContext.ts';
import Collapsible from '@/components/generic/Collapsible.vue';

// Selektions-State + Edit-Aktion aus dem von useOsmdInteractiveScore bereitgestellten Kontext
// (analog ChordEditView) – kein Prop-Drilling/Emit mehr.
const {
  currentKeySignature,
  selectedMeasureIndex: measureIndex,
  selectedMeasure: measureNumber,
  saveKeySignature,
} = injectScoreEditContext();


const selectedFifths = ref(0);
const selectedMode = ref<'major' | 'minor'>('major');

// Current key display
const currentKeyName = computed(() => getKeyDisplayName(selectedFifths.value, selectedMode.value));
const accidentalsText = computed(() => getAccidentalsDescription(selectedFifths.value));

// Parse existing key signature when the selected measure / current key signature changes
watch(() => currentKeySignature.value, (keySignature) => {
  if (keySignature) {
    selectedFifths.value = keySignature.fifths;
    selectedMode.value = keySignature.mode;
  } else {
    // Reset to C major
    selectedFifths.value = 0;
    selectedMode.value = 'major';
  }
}, {immediate: true});

// Auto-Save (analog ChordEditView): Änderungen an Vorzeichen/Tongeschlecht werden debounced
// übernommen – kein manueller "Speichern"-Button mehr.
watchDebounced([selectedFifths, selectedMode], handleSave, {debounce: 300});

function handleSave() {
  if (measureNumber.value !== null) {
    saveKeySignature({
      measureNumber: measureNumber.value,
      measureIndex: measureIndex.value ?? undefined,
      fifths: selectedFifths.value,
      mode: selectedMode.value,
    });
  }
}

// Quick select common keys
const quickKeys: { fifths: number; mode: 'major' | 'minor'; label: string }[] = [
  {fifths: 0, mode: 'major', label: 'C Dur'},
  {fifths: 1, mode: 'major', label: 'G Dur'},
  {fifths: 2, mode: 'major', label: 'D Dur'},
  {fifths: -1, mode: 'major', label: 'F Dur'},
  {fifths: -2, mode: 'major', label: 'Bb Dur'},
  {fifths: 0, mode: 'minor', label: 'A Moll'},
  {fifths: 1, mode: 'minor', label: 'E Moll'},
  {fifths: 2, mode: 'minor', label: 'B Moll'},
];

function selectQuickKey(key: { fifths: number; mode: 'major' | 'minor' }) {
  selectedFifths.value = key.fifths;
  selectedMode.value = key.mode;
}

// Visual representation of sharps/flats
const sharpsOrder = ['F', 'C', 'G', 'D', 'A', 'E', 'B'];
const flatsOrder = ['B', 'E', 'A', 'D', 'G', 'C', 'F'];

const activeAccidentals = computed(() => {
  if (selectedFifths.value > 0) {
    return sharpsOrder.slice(0, selectedFifths.value).map(note => note + '#');
  } else if (selectedFifths.value < 0) {
    return flatsOrder.slice(0, Math.abs(selectedFifths.value)).map(note => note + 'b');
  }
  return [];
});
</script>

<template>
  <Transition
      enter-active-class="animate-fade animate-duration-200 animate-ease-out"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
  >
  <div
      v-if="measureNumber !== null"
      class="flex items-center justify-center p-2 overflow-auto"
  >
  <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden ring-1 ring-black/5">

  <!-- Header -->
  <div class="flex items-center justify-between p-5 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-orange-50">
    <div>
      <h2 class="text-xl font-bold text-gray-800 flex items-center gap-3">
        <div class="p-2 bg-amber-100 text-amber-600 rounded-lg">
          <Icon icon="fa6-solid:key" class="text-lg"/>
        </div>
        Tonart bearbeiten
      </h2>
      <p class="text-sm text-gray-600 mt-1">Ab Takt {{ measureNumber }}</p>
    </div>
  </div>

  <!-- Content -->
  <div class="p-5 space-y-5">
  <div class="flex gap-2 mb-2">
    <!-- Key Preview -->
    <div class="flex  shrink-0 grow-1 flex-col items-center text-center p-2 bg-gradient-to-r from-amber-100 to-orange-100 rounded-xl  ">
      <div class="flex grow text-lg font-bold text-amber-800">{{ currentKeyName }}</div>
      <div class="text-sm text-amber-600 mt-1">{{ accidentalsText }}</div>
      <div v-if="activeAccidentals.length" class="flex justify-center gap-2 mt-3">
              <span
                  v-for="acc in activeAccidentals"
                  :key="acc"
                  class="px-2 py-1 bg-white/60 rounded text-sm font-mono text-amber-700"
              >
                {{ acc }}
              </span>
      </div>
    </div>

    <!-- Quick Select -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-2">Schnellauswahl</label>
      <div class="flex flex-wrap gap-2">
        <button
            v-for="key in quickKeys"
            :key="`${key.fifths}-${key.mode}`"
            @click="selectQuickKey(key)"
            class="px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors"
            :class="selectedFifths === key.fifths && selectedMode === key.mode
                  ? 'bg-amber-100 border-amber-300 text-amber-700'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'"
        >
          {{ key.label }}
        </button>
      </div>
    </div>
  </div>
  <!-- Detail-Editor (eingeklappt; Preview + Schnellauswahl decken den Normalfall ab) -->
  <Collapsible variant="default" class="text-sm text-blue-500" :is-open="false">
  <template #header>
    <h5 class="text-lg">Alle Tonarten</h5>
  </template>
  <template #default>

    <!-- Mode Selection -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-2">Tongeschlecht</label>
      <div class="flex gap-2">
        <button
            @click="selectedMode = 'major'"
            class="flex-1 py-3 text-sm font-medium rounded-lg border transition-colors"
            :class="selectedMode === 'major'
                  ? 'bg-amber-500 border-amber-500 text-white'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'"
        >
          <Icon icon="fa6-solid:sun" class="mr-2"/>
          Dur (Major)
        </button>
        <button
            @click="selectedMode = 'minor'"
            class="flex-1 py-3 text-sm font-medium rounded-lg border transition-colors"
            :class="selectedMode === 'minor'
                  ? 'bg-amber-500 border-amber-500 text-white'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'"
        >
          <Icon icon="fa6-solid:moon" class="mr-2"/>
          Moll (Minor)
        </button>
      </div>
    </div>

    <!-- Fifths Slider -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-2">
        Vorzeichen
        <span class="text-gray-400 font-normal ml-2">
                ({{ selectedFifths > 0 ? 'Kreuze' : selectedFifths < 0 ? 'Bs' : 'Keine' }})
              </span>
      </label>
      <div class="space-y-3">
        <!-- Slider -->
        <div class="relative">
          <input
              type="range"
              min="-7"
              max="7"
              step="1"
              v-model.number="selectedFifths"
              class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber-500"
          />
          <div class="flex justify-between text-xs text-gray-400 mt-1">
            <span>7b</span>
            <span>0</span>
            <span>7#</span>
          </div>
        </div>

        <!-- Key Signature Buttons -->
        <div class="grid grid-cols-5 gap-1">
          <button
              v-for="keySig in KEY_SIGNATURES"
              :key="keySig.fifths"
              @click="selectedFifths = keySig.fifths"
              class="py-2 text-xs font-medium rounded border transition-colors text-center"
              :class="selectedFifths === keySig.fifths
                    ? 'bg-amber-500 border-amber-500 text-white'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'"
          >
            <div class="font-semibold">
              {{ selectedMode === 'major' ? keySig.major : keySig.minor }}
            </div>
            <div class="text-[10px] opacity-70">
              {{ keySig.fifths > 0 ? keySig.fifths + '#' : keySig.fifths < 0 ? Math.abs(keySig.fifths) + 'b' : '-' }}
            </div>
          </button>
        </div>
      </div>
    </div>
  </template>
  </Collapsible>
  </div>
  </div>
  </div>
  </Transition>
</template>
